import { ReturnDesiredAction, ReturnItemStatus, ReturnReason } from '@sleed/bizcommerce-api';

/**
 * One row of the returns queue — **per piece**, mirroring `ReturnGridRowToken`.
 *
 * The keys are PascalCase on purpose. Responses are camelCase (the API's global naming policy) but
 * `DataSourceLoader` resolves sort, group and filter selectors against the **CLR** property names,
 * and the Δ-06 allow-list is generated from exactly those names. Normalising the row back to
 * PascalCase lets one name serve as the column key, as the sort selector and as the filter selector,
 * so a column and the thing the server permits filtering on cannot drift apart.
 *
 * The generator DOES type `ReturnGridResponse.data` as `Array<ReturnGridRowToken>`; this interface is
 * not a substitute for it but the PascalCase view of it, which is the form the selectors need. Keep the
 * two in step — a field renamed server-side changes the token and must be renamed here as well.
 */
export interface ReturnGridRow {
	Id: number;
	ReturnRequestId: number;
	Reference: string;
	OrderId: number | null;
	OrderUID: string;
	PaymentMethod: string | null;
	DeliveryMethod: string | null;
	CustomerFirstName: string | null;
	CustomerLastName: string | null;
	CustomerEmail: string;
	CustomerPhone: string | null;
	CustomerVatNumber: string | null;
	ProductId: number;
	ProductName: string;
	ProductCode: string | null;
	ProductBarcode: string | null;
	Variant: string | null;
	PieceIndex: number;
	UnitValue: number;
	Reason: ReturnReason;
	DesiredAction: ReturnDesiredAction;
	RequestedReplacement: string | null;
	ReplacementDispatchedAt: string | null;
	ReplacementReference: string | null;
	RefundExecutedAt: string | null;
	Status: ReturnItemStatus;
	SubmittedAt: string;
	StatusChangedAt: string | null;
	StatusChangedByUserName: string | null;
	AdminNotes: string | null;
}

/** Enums and amounts whose zero value `DefaultIgnoreCondition = WhenWritingDefault` omits. */
const ZERO_DEFAULTS = {
	Reason: ReturnReason.None,
	DesiredAction: ReturnDesiredAction.None,
	UnitValue: 0,
	PieceIndex: 0
};

/**
 * camelCase response row → {@link ReturnGridRow}. Only the first character needs flipping: every
 * property of the token round-trips through it, `OrderUID` included.
 */
export function toGridRow(raw: unknown): ReturnGridRow {
	const source = (raw ?? {}) as Record<string, unknown>;
	const normalized: Record<string, unknown> = {};

	for (const key of Object.keys(source)) normalized[key.charAt(0).toUpperCase() + key.slice(1)] = source[key];

	return { ...ZERO_DEFAULTS, ...normalized } as ReturnGridRow;
}

/** Full name, or the empty string when the order carried neither part. */
export const customerName = (row: Pick<ReturnGridRow, 'CustomerFirstName' | 'CustomerLastName'>) =>
	[row.CustomerFirstName, row.CustomerLastName].filter(Boolean).join(' ');
